/** Coquille de l'application : barre supérieure, onglets, contenu. */

import type { ReactNode } from 'react'
import { NavLink, Outlet } from 'react-router-dom'

import { useAuth } from '@/hooks/useAuth'
import { Spinner, cx } from './ui'
import styles from './Layout.module.css'

export type StatusTone = 'idle' | 'busy' | 'ok' | 'error'

const DocumentIcon = () => (
  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M14 3v5h5" />
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h9l5 5v11a2 2 0 01-2 2z" />
  </svg>
)

const GearIcon = () => (
  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 00.3 1.9l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.9-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 11-4 0v-.1A1.7 1.7 0 008.9 19a1.7 1.7 0 00-1.9.4l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.9 1.7 1.7 0 00-1.5-1H3a2 2 0 110-4h.1A1.7 1.7 0 005 8.9a1.7 1.7 0 00-.4-1.9l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.9.3H9a1.7 1.7 0 001-1.5V3a2 2 0 114 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.9-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.9V9a1.7 1.7 0 001.5 1H21a2 2 0 110 4h-.1a1.7 1.7 0 00-1.5 1z" />
  </svg>
)

export function StatusPill({ tone, children }: { tone: StatusTone; children: ReactNode }) {
  const dot =
    tone === 'busy' ? styles.dotBusy : tone === 'ok' ? styles.dotOk : tone === 'error' ? styles.dotErr : undefined
  return (
    <span className={styles.status} role="status" aria-live="polite">
      <span className={cx(styles.dot, dot)} />
      {children}
    </span>
  )
}

export function Layout({ status }: { status?: ReactNode }) {
  const { user, logout } = useAuth()

  return (
    <>
      <header className={styles.topbar}>
        <div className={styles.inner}>
          <NavLink to="/" className={styles.brand}>
            <span className={styles.brandName}>Youkapi</span>
            <span className={styles.brandApp}>Convertisseur de catalogue</span>
          </NavLink>

          <nav className={styles.tabs} aria-label="Sections">
            <NavLink to="/" end className={({ isActive }) => cx(styles.tab, isActive && styles.tabActive)}>
              <DocumentIcon />
              <span className={styles.tabLabel}>Conversion</span>
            </NavLink>
            <NavLink to="/regles" className={({ isActive }) => cx(styles.tab, isActive && styles.tabActive)}>
              <GearIcon />
              <span className={styles.tabLabel}>Règles</span>
            </NavLink>
          </nav>

          <div className={styles.spacer} />
          {status}
          <div className={styles.user}>
            <span>{user?.username}</span>
            <button type="button" className={styles.logout} onClick={() => void logout()}>
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className={styles.page}>
        <Outlet />
      </main>

      <p className={styles.footnote}>
        Les fichiers générés sont renvoyés directement au navigateur : rien n’est conservé sur le serveur.
      </p>
    </>
  )
}

export function BootScreen({ children }: { children: ReactNode }) {
  return (
    <div className={styles.boot}>
      <Spinner />
      <span>{children}</span>
    </div>
  )
}
